import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  Zap,
  Target,
  Headphones,
  RefreshCw,
  Sparkles,
  MapPin,
  CreditCard,
  QrCode,
  Smartphone,
  Navigation,
  ArrowRight,
  Star,
} from "lucide-react";

export default function DemoPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => {
      setShowSplash(false);
      // 启动画面结束后，触发卡片动画
      setTimeout(() => {
        const cards = document.querySelectorAll(".feature-card");
        cards.forEach((card, index) => {
          setTimeout(() => {
            card.classList.add("visible");
          }, index * 150); // 每个卡片延迟150ms
        });
      }, 300);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleExploreDemo = () => {
    window.location.href = "/";
  };

  const highlights = [
    {
      icon: <CheckCircle className="feature-icon" />,
      title: "Trusted Information",
      text: "City tourism board—approved tips you can trust.",
    },
    {
      icon: <Zap className="feature-icon" />,
      title: "AI-Powered Search",
      text: "Instant AI search with precise answers.",
    },
    {
      icon: <Target className="feature-icon" />,
      title: "Personalized Itineraries",
      text: "Tailored travel routes based on traveler's interests, schedule, and weather.",
    },
    {
      icon: <Headphones className="feature-icon" />,
      title: "Real-Time Audio",
      text: "Real-time navigation with trilingual audio guides.",
    },
    {
      icon: <RefreshCw className="feature-icon" />,
      title: "Adaptive Solutions",
      text: "Smart itinerary alternatives for unexpected changes during trip.",
    },
  ];

  const steps = [
    {
      icon: <MapPin className="step-icon" />,
      text: "Choose Your Destination & Hotel",
      detail: "Select directly on City Getaways.",
    },
    {
      icon: <CreditCard className="step-icon" />,
      text: "Confirm Your Booking",
      detail: "Secure your trip with online payment.",
    },
    {
      icon: <QrCode className="step-icon" />,
      text: "Get Your Personalized QR Code",
      detail: "Based on your dates, interests, preferences.",
    },
    {
      icon: <Smartphone className="step-icon" />,
      text: "Scan & Access the App",
      detail: "Open your personal itinerary instantly.",
    },
    {
      icon: <Navigation className="step-icon" />,
      text: "Enjoy AI-Powered Guided Travel",
      detail: "Navigation, multilingual audio, and instant backup plans.",
    },
  ];

  return (
    <div className="demo-page">
      <style>{`
        /* Premium Color Palette */
        :root {
          --bg-dark: #0a0e27;
          --bg-light: #ffffff;
          --text-light: #ffffff;
          --text-dark: #0a0e27;
          --text-muted: #6b7280;
          --gold: #fbbf24;
          --gold-dark: #f59e0b;
          --purple: #8b5cf6;
          --blue-dark: #1e40af;
          --icon-gray: #9ca3af;
          --icon-light: #e5e7eb;
          --icon-bg-light: #f3f4f6;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        /* Main Container */
        .demo-page {
          width: 100vw;
          height: 100vh;
          background: var(--bg-dark);
          font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Arial, sans-serif;
          overflow: hidden;
          position: relative;
        }

        /* Premium Entrance Animation */
        .splash-screen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
          display: ${showSplash ? "flex" : "none"};
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          animation: ${
            showSplash ? "fadeOut 0.5s ease-out 2s forwards" : "none"
          };
        }

        .splash-content {
          text-align: center;
          animation: scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .splash-title {
          font-size: 5rem;
          font-weight: 200;
          letter-spacing: 0.5em;
          margin-bottom: 1rem;
          background: linear-gradient(90deg, var(--gold), var(--gold-dark), var(--gold));
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 2s linear infinite;
        }

        .splash-subtitle {
          font-size: 1.1rem;
          color: #9ca3af;
          letter-spacing: 0.3em;
          font-weight: 300;
          opacity: 0;
          animation: fadeIn 0.6s ease-out 0.3s forwards;
        }

        .splash-tagline {
          font-size: 0.9rem;
          color: #6b7280;
          margin-top: 2rem;
          letter-spacing: 0.2em;
          font-weight: 300;
          opacity: 0;
          animation: fadeIn 0.6s ease-out 0.6s forwards;
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          to {
            opacity: 0;
            visibility: hidden;
          }
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        /* Landscape Layout - Premium 3 Column */
        .main-content {
          height: 100vh;
          display: grid;
          grid-template-columns: 420px 1fr 1fr;
          gap: 0;
          opacity: ${isVisible && !showSplash ? 1 : 0};
          transition: opacity 0.8s ease-out;
        }

        /* Left Panel - Hero with Immersive Background */
        .hero {
          background: 
            linear-gradient(rgba(10, 14, 39, 0.7), rgba(10, 14, 39, 0.85)),
            url('https://aebc975c.delivery.rocketcdn.me/wp-content/uploads/2021/12/marc-olivier-jodoin-BG9oZ15a4Xk-unsplash-1-scaled.jpg.webp') center/cover;
          padding: 50px 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(180deg, transparent 0%, rgba(251, 191, 36, 0.1) 100%);
          pointer-events: none;
        }

        .hero-content {
          position: relative;
          z-index: 1;
        }

        .hero h2 {
          font-size: 3.2rem;
          font-weight: 300;
          letter-spacing: -0.5px;
          margin-bottom: 24px;
          line-height: 1.1;
          animation: slideInLeft 0.8s ease-out;
          text-shadow: 0 2px 20px rgba(0,0,0,0.3);
        }

        .hero h2 span {
          background: linear-gradient(90deg, var(--gold), var(--gold-dark));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero p {
          font-size: 1.1rem;
          line-height: 1.7;
          opacity: 0.95;
          margin-bottom: 36px;
          font-weight: 300;
          animation: slideInLeft 0.8s ease-out 0.2s both;
          text-shadow: 0 1px 10px rgba(0,0,0,0.3);
        }

        .btn-cta {
          background: linear-gradient(135deg, #f59e0b, #fbbf24, #f59e0b);
          background-size: 200% auto;
          color: var(--bg-dark);
          border: none;
          padding: 18px 42px;
          font-size: 1.05rem;
          font-weight: 700;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-flex;
          align-items: center;
          gap: 10px;
          animation: slideInLeft 0.8s ease-out 0.4s both, pulse 2s infinite;
          width: fit-content;
          box-shadow: 
            0 4px 20px rgba(245, 158, 11, 0.4),
            0 8px 40px rgba(251, 191, 36, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
          position: relative;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .btn-cta::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 50px;
          background: radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          filter: blur(20px);
          opacity: 0.8;
          z-index: -1;
        }

        .btn-cta:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 
            0 6px 30px rgba(245, 158, 11, 0.5),
            0 12px 50px rgba(251, 191, 36, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
          background-position: right center;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-30px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Middle Panel - Premium Features */
        .highlights {
          background: #f8f9fa;
          padding: 35px 30px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .highlights h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--text-dark);
          flex-shrink: 0;
        }

        .highlights h3 svg {
          color: var(--gold-dark);
        }

        .highlights-grid {
          display: flex;
          flex-direction: column;
          gap: 14px;
          flex: 1;
          justify-content: space-between;
        }

        .feature-card {
          background: white;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 18px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          opacity: 0;
          transform: translateY(20px);
          flex: 1;
          max-height: calc((100vh - 180px) / 5);
        }

        .feature-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .feature-card:hover {
          transform: translateY(-3px) scale(1.01);
          box-shadow: 0 12px 30px rgba(0,0,0,0.12);
        }

        .feature-card.visible:hover {
          transform: translateY(-3px) scale(1.01);
        }

        .feature-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: var(--icon-bg-light);
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
        }

        .feature-card:hover .feature-icon-wrapper {
          transform: rotate(3deg) scale(1.08);
          background: #e5e7eb;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .feature-icon {
          width: 24px;
          height: 24px;
          color: var(--icon-gray);
        }

        .card-content {
          flex: 1;
          min-width: 0;
        }

        .card-content h4 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 4px;
          color: var(--text-dark);
          transition: color 0.3s ease;
        }

        .feature-card:hover .card-content h4 {
          color: var(--gold-dark);
        }

        .card-content p {
          font-size: 0.82rem;
          color: var(--text-muted);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Right Panel - Timeline */
        .how-it-works {
          background: linear-gradient(135deg, #1a1f3a 0%, #0a0e27 100%);
          padding: 35px 30px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          color: white;
        }

        .how-it-works h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
          flex-shrink: 0;
        }

        .how-it-works h3 svg {
          color: var(--gold);
        }

        .steps {
          display: flex;
          flex-direction: column;
          gap: 0;
          flex: 1;
          justify-content: space-between;
          position: relative;
        }

        .steps::before {
          content: '';
          position: absolute;
          left: 24px;
          top: 40px;
          bottom: 40px;
          width: 2px;
          background-image: repeating-linear-gradient(
            180deg,
            #6b7280,
            #6b7280 8px,
            transparent 8px,
            transparent 16px
          );
          opacity: 0.3;
        }

        .step {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 14px 0 14px 0;
          position: relative;
          transition: all 0.3s ease;
          flex: 1;
          max-height: calc((100vh - 200px) / 5);
        }

        .step:hover {
          transform: translateX(8px);
        }

        .step-number {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--bg-dark);
          border: 3px solid #4b5563;
          color: #d1d5db;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.1rem;
          flex-shrink: 0;
          transition: all 0.3s ease;
          position: relative;
          z-index: 2;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }

        .step:hover .step-number {
          background: #374151;
          border-color: #6b7280;
          transform: scale(1.15);
          box-shadow: 0 6px 25px rgba(0,0,0,0.4);
          color: white;
        }

        .step-content {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 16px;
          min-width: 0;
        }

        .step-icon {
          width: 22px;
          height: 22px;
          color: #9ca3af;
          flex-shrink: 0;
          transition: color 0.3s ease;
        }

        .step:hover .step-icon {
          color: #d1d5db;
        }

        .step-text-wrapper {
          flex: 1;
          min-width: 0;
        }

        .step-text {
          font-size: 0.95rem;
          font-weight: 500;
          color: white;
          margin-bottom: 4px;
        }

        .step-detail {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.7);
        }

        /* Animation delays with stagger effect */
        .feature-card {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .feature-card:nth-child(1) { transition-delay: 0.1s; }
        .feature-card:nth-child(2) { transition-delay: 0.2s; }
        .feature-card:nth-child(3) { transition-delay: 0.3s; }
        .feature-card:nth-child(4) { transition-delay: 0.4s; }
        .feature-card:nth-child(5) { transition-delay: 0.5s; }
        
        .step {
          animation: fadeInRight 0.6s ease-out both;
        }
        
        .step:nth-child(1) { animation-delay: 0.1s; }
        .step:nth-child(2) { animation-delay: 0.2s; }
        .step:nth-child(3) { animation-delay: 0.3s; }
        .step:nth-child(4) { animation-delay: 0.4s; }
        .step:nth-child(5) { animation-delay: 0.5s; }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Responsive adjustments */
        @media screen and (max-width: 1194px) {
          .main-content {
            grid-template-columns: 360px 1fr 1fr;
          }
          
          .hero h2 {
            font-size: 2.6rem;
          }
          
          .hero, .highlights, .how-it-works {
            padding: 35px 30px;
          }
        }
      `}</style>

      {/* Premium Entrance Animation */}
      {showSplash && (
        <div className="splash-screen">
          <div className="splash-content">
            <h1 className="splash-title">CITYGETAWAY</h1>
            <p className="splash-subtitle">AI-POWERED TRAVEL</p>
            <p className="splash-tagline">
              Designed by Concord • Montreal 2025
            </p>
          </div>
        </div>
      )}

      {/* Main Content - Premium 3 Column Layout */}
      <div className="main-content">
        {/* Left Panel - Immersive Hero */}
        <section className="hero">
          <div className="hero-content">
            <h2>
              Worry-free, <span>Pack & Go.</span>
            </h2>
            <p>
              An AI-powered smart travel platform for Free Independent Travelers
              (FIT), delivering fast and reliable city insights for a smoother,
              smarter trip.
            </p>
            <button className="btn-cta" onClick={handleExploreDemo}>
              Explore Demo
              <ArrowRight size={18} />
            </button>
          </div>
        </section>

        {/* Middle Panel - Premium Features */}
        <section className="highlights">
          <h3>
            <Sparkles size={28} /> Highlights
          </h3>
          <div className="highlights-grid">
            {highlights.map((item, idx) => (
              <div key={idx} className="feature-card">
                <div className="feature-icon-wrapper">
                  {item.icon}
                </div>
                <div className="card-content">
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Right Panel - Premium Timeline */}
        <section className="how-it-works">
          <h3>
            <Star size={28} /> How It Works
          </h3>
          <div className="steps">
            {steps.map((step, idx) => (
              <div key={idx} className="step">
                <div className="step-number">{idx + 1}</div>
                <div className="step-content">
                  {step.icon}
                  <div className="step-text-wrapper">
                    <div className="step-text">{step.text}</div>
                    <div className="step-detail">{step.detail}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}